import { FC, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Minus, AlertTriangle, Search, Loader2 } from 'lucide-react';
import { ManualVoucherFormProps, ItemFormData } from './ManualVoucherForm.types';
import { ManualVoucherData, VoucherItem } from '../../types';
import { locationsService } from '@/pages/locations/services/locations.service';
import { posService } from '@/services/pos.service';
import { useAuth } from '@/context/AuthContext';
import { DecimalKeypad, TextKeypad } from '@/components/ui/keypads';
import { DNIKeypad } from '@/components/ui/keypads';

export const ManualVoucherForm: FC<ManualVoucherFormProps> = ({ onSubmit, loading = false, showCancelButton = true }) => {
  const { user } = useAuth();
  const isCajero = user?.positionId === 3;

  const [formData, setFormData] = useState<ManualVoucherData>({
    voucherType: 'boleta',
    isContingency: false,
    customer: {
      name: '',
      documentType: 'dni',
      documentNumber: '',
      address: '',
      email: ''
    },
    items: [],
    observations: ''
  });

  const [newItem, setNewItem] = useState<ItemFormData>({
    description: '',
    quantity: '1',
    unitPrice: '0.00'
  });

  const [locations, setLocations] = useState<{id: string, name: string}[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);

  const [customSeries, setCustomSeries] = useState('');
  const [showCustomSeries, setShowCustomSeries] = useState(false);

  const [activeKeypad, setActiveKeypad] = useState<{ type: 'text' | 'decimal' | 'dni'; field: string; title: string } | null>(null);
  const [keypadInitialValue, setKeypadInitialValue] = useState('');

  const openKeypad = (type: 'text' | 'decimal' | 'dni', field: string, title: string, currentValue: string) => {
    setActiveKeypad({ type, field, title });
    setKeypadInitialValue(currentValue);
  };

  const handleKeypadSubmit = (value: string | number) => {
    if (!activeKeypad) return;
    const strValue = value.toString();

    switch (activeKeypad.field) {
      case 'customerName':
        setFormData(prev => ({ ...prev, customer: { ...prev.customer, name: strValue } }));
        break;
      case 'documentNumber':
        handleDocumentChange(strValue);
        break;
      case 'email':
        setFormData(prev => ({ ...prev, customer: { ...prev.customer, email: strValue } }));
        break;
      case 'address':
        setFormData(prev => ({ ...prev, customer: { ...prev.customer, address: strValue } }));
        break;
      case 'customSeries':
        setCustomSeries(strValue.toUpperCase());
        break;
      case 'itemDescription':
        setNewItem(prev => ({ ...prev, description: strValue }));
        break;
      case 'itemQuantity':
        setNewItem(prev => ({ ...prev, quantity: strValue }));
        break;
      case 'itemUnitPrice':
        setNewItem(prev => ({ ...prev, unitPrice: strValue }));
        break;
      case 'observations':
        setFormData(prev => ({ ...prev, observations: strValue }));
        break;
    }
    setActiveKeypad(null);
  };

  // Cargar ubicaciones al montar el componente
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoadingLocations(true);
        const locationsData = await locationsService.getLocations();
        setLocations(locationsData.map(loc => ({
          id: loc.id,
          name: loc.nombre
        })));
      } catch (error) {
        console.error('Error loading locations:', error);
        // Fallback a datos mock en caso de error
        setLocations([
          { id: 'loc-001', name: 'Local Centro' },
          { id: 'loc-002', name: 'Local Norte' },
          { id: 'loc-003', name: 'Local Sur' }
        ]);
      } finally {
        setLoadingLocations(false);
      }
    };

    loadLocations();
  }, []);

  // Función para buscar cliente por documento
  const searchCustomer = async (document: string) => {
    if (document.length < 8) return;
    
    setIsSearchingCustomer(true);
    
    try {
      const result = await posService.searchCustomer(document);
      if (result.success && result.data) {
        // Cliente encontrado, rellenar campos
        const customer = result.data;
        setFormData(prev => ({
          ...prev,
          customer: {
            ...prev.customer,
            name: customer.name || '',
            email: customer.email || '',
            address: customer.address || ''
          }
        }));
        setCustomerFound(true);
      } else {
        // Cliente no encontrado, mantener documento pero limpiar otros campos
        setFormData(prev => ({
          ...prev,
          customer: {
            ...prev.customer,
            name: '',
            email: '',
            address: ''
          }
        }));
        setCustomerFound(false);
      }
    } catch (error) {
      console.error('Error searching customer:', error);
      setCustomerFound(false);
    } finally {
      setIsSearchingCustomer(false);
    }
  };

  // Manejar cambios en el documento del cliente
  const handleDocumentChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      customer: { ...prev.customer, documentNumber: value }
    }));
    
    // Auto-buscar cuando el documento tenga 8 dígitos (DNI) o 11 dígitos (RUC)
    if (value.length === 8 || value.length === 11) {
      searchCustomer(value);
    } else {
      setCustomerFound(false);
    }
  };

  const addItem = () => {
    if (!newItem.description.trim()) return;

    const quantity = parseFloat(newItem.quantity) || 1;
    const unitPrice = parseFloat(newItem.unitPrice) || 0;
    const subtotal = quantity * unitPrice;

    const item: VoucherItem = {
      id: `item-${Date.now()}`,
      description: newItem.description.trim(),
      quantity,
      unitPrice,
      subtotal
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));

    setNewItem({
      description: '',
      quantity: '1',
      unitPrice: '0.00'
    });
  };

  const removeItem = (itemId: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const totalAmount = formData.items.reduce((sum, item) => sum + item.subtotal, 0);
  const igv = totalAmount * 0.18;
  const grandTotal = totalAmount + igv;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      alert('Debe agregar al menos un ítem');
      return;
    }

    if (formData.voucherType === 'factura' && (!formData.customer.name || !formData.customer.documentNumber || !formData.customer.address)) {
      alert('Para facturas son requeridos: nombre, RUC y dirección');
      return;
    }

    const submitData = {
      ...formData,
      series: showCustomSeries ? customSeries : undefined
    };

    onSubmit(submitData);
  };

  const isValidContingencySeries = (series: string) => {
    return series && !series.startsWith('F') && !series.startsWith('B');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Datos del Comprobante</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="voucherType">Tipo de Comprobante *</Label>
            <Select
              value={formData.voucherType}
              onValueChange={(value: 'boleta' | 'factura') => setFormData(prev => ({ ...prev, voucherType: value }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="boleta">Boleta</SelectItem>
                <SelectItem value="factura">Factura</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="locationId">Local</Label>
            <Select
              value={formData.locationId || 'none'}
              onValueChange={(value) => setFormData(prev => ({ ...prev, locationId: value === 'none' ? undefined : value }))}
              disabled={loadingLocations}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={loadingLocations ? "Cargando ubicaciones..." : "Seleccionar local"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seleccionar local</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isContingency"
              checked={formData.isContingency}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isContingency: !!checked }))}
            />
            <Label htmlFor="isContingency" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Emisión por Contingencia
            </Label>
          </div>

          {formData.isContingency && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800 mb-3">
                Para comprobantes de contingencia, la serie no debe empezar con "F" ni "B"
              </p>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="customSeries"
                  checked={showCustomSeries}
                  onCheckedChange={(checked) => setShowCustomSeries(!!checked)}
                />
                <Label htmlFor="customSeries">Especificar serie personalizada</Label>
              </div>
              
              {showCustomSeries && (
                <div className="mt-3">
                  <Input
                    placeholder="Ej: C001, X001"
                    value={customSeries}
                    onChange={(e) => setCustomSeries(e.target.value.toUpperCase())}
                    readOnly={isCajero}
                    onClick={isCajero ? () => openKeypad('text', 'customSeries', 'Serie Personalizada', customSeries) : undefined}
                    className={`${!isValidContingencySeries(customSeries) ? 'border-red-300' : ''} ${isCajero ? 'cursor-pointer' : ''}`}
                  />
                  {customSeries && !isValidContingencySeries(customSeries) && (
                    <p className="text-sm text-red-600 mt-1">
                      La serie no debe empezar con "F" ni "B"
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Datos del Cliente</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="documentType">Tipo de Documento</Label>
            <Select
              value={formData.customer.documentType}
              onValueChange={(value: 'dni' | 'ruc') => setFormData(prev => ({
                ...prev,
                customer: { ...prev.customer, documentType: value }
              }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dni">DNI</SelectItem>
                <SelectItem value="ruc">RUC</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="customerName">
              {formData.voucherType === 'factura' ? 'Razón Social *' : 'Nombre'}
            </Label>
            <Input
              id="customerName"
              value={formData.customer.name}
              onChange={!isCajero ? (e) => setFormData(prev => ({
                ...prev,
                customer: { ...prev.customer, name: e.target.value }
              })) : undefined}
              readOnly={isCajero}
              onClick={isCajero ? () => openKeypad('text', 'customerName', formData.voucherType === 'factura' ? 'Razón Social' : 'Nombre', formData.customer.name) : undefined}
              className={isCajero ? 'cursor-pointer' : ''}
              required={formData.voucherType === 'factura'}
            />
          </div>

          <div>
            <Label htmlFor="documentNumber">
              {formData.customer.documentType === 'ruc' ? 'RUC *' : 'DNI'}
            </Label>
            <div className="relative">
              <Input
                id="documentNumber"
                value={formData.customer.documentNumber}
                onChange={!isCajero ? (e) => handleDocumentChange(e.target.value) : undefined}
                readOnly={isCajero}
                onClick={isCajero ? () => openKeypad('dni', 'documentNumber', formData.customer.documentType === 'ruc' ? 'RUC' : 'DNI', formData.customer.documentNumber) : undefined}
                maxLength={formData.customer.documentType === 'ruc' ? 11 : 8}
                required={formData.voucherType === 'factura'}
                className={`${customerFound && formData.customer.documentNumber ? 'border-green-500 bg-green-50' : ''} ${isCajero ? 'cursor-pointer' : ''}`}
              />
              {isSearchingCustomer && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                </div>
              )}
              {customerFound && formData.customer.documentNumber && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Search className="h-4 w-4 text-green-500" />
                </div>
              )}
            </div>
            {customerFound && formData.customer.documentNumber && (
              <div className="text-sm text-green-600 bg-green-50 p-2 rounded-lg mt-1">
                ✓ Cliente encontrado en la base de datos
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type={isCajero ? 'text' : 'email'}
              value={formData.customer.email || ''}
              onChange={!isCajero ? (e) => setFormData(prev => ({
                ...prev,
                customer: { ...prev.customer, email: e.target.value }
              })) : undefined}
              readOnly={isCajero}
              onClick={isCajero ? () => openKeypad('text', 'email', 'Email', formData.customer.email || '') : undefined}
              className={isCajero ? 'cursor-pointer' : ''}
            />
          </div>

          {formData.voucherType === 'factura' && (
            <div className="md:col-span-2">
              <Label htmlFor="address">Dirección *</Label>
              <Input
                id="address"
                value={formData.customer.address || ''}
                onChange={!isCajero ? (e) => setFormData(prev => ({
                  ...prev,
                  customer: { ...prev.customer, address: e.target.value }
                })) : undefined}
                readOnly={isCajero}
                onClick={isCajero ? () => openKeypad('text', 'address', 'Dirección', formData.customer.address || '') : undefined}
                className={isCajero ? 'cursor-pointer' : ''}
                required
              />
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Ítems del Comprobante</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
          <div className="md:col-span-5">
            <Label htmlFor="itemDescription">Descripción</Label>
            <Input
              id="itemDescription"
              value={newItem.description}
              onChange={!isCajero ? (e) => setNewItem(prev => ({ ...prev, description: e.target.value })) : undefined}
              readOnly={isCajero}
              onClick={isCajero ? () => openKeypad('text', 'itemDescription', 'Descripción del Ítem', newItem.description) : undefined}
              placeholder="Descripción del ítem"
              className={isCajero ? 'cursor-pointer' : ''}
            />
          </div>
          
          <div className="md:col-span-2">
            <Label htmlFor="quantity">Cantidad</Label>
            <Input
              id="quantity"
              type={isCajero ? 'text' : 'number'}
              min={isCajero ? undefined : '1'}
              step={isCajero ? undefined : '1'}
              value={newItem.quantity}
              onChange={!isCajero ? (e) => setNewItem(prev => ({ ...prev, quantity: e.target.value })) : undefined}
              readOnly={isCajero}
              onClick={isCajero ? () => openKeypad('decimal', 'itemQuantity', 'Cantidad', newItem.quantity) : undefined}
              className={isCajero ? 'cursor-pointer' : ''}
            />
          </div>
          
          <div className="md:col-span-3">
            <Label htmlFor="unitPrice">Precio Unitario</Label>
            <Input
              id="unitPrice"
              type={isCajero ? 'text' : 'number'}
              min={isCajero ? undefined : '0'}
              step={isCajero ? undefined : '0.01'}
              value={newItem.unitPrice}
              onChange={!isCajero ? (e) => setNewItem(prev => ({ ...prev, unitPrice: e.target.value })) : undefined}
              readOnly={isCajero}
              onClick={isCajero ? () => openKeypad('decimal', 'itemUnitPrice', 'Precio Unitario', newItem.unitPrice) : undefined}
              className={isCajero ? 'cursor-pointer' : ''}
            />
          </div>
          
          <div className="md:col-span-2 flex items-end justify-center">
            <Button
              type="button"
              onClick={addItem}
              disabled={!newItem.description.trim()}
              size="sm"
              className="px-3 py-2"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {formData.items.length > 0 && (
          <div className="space-y-2">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700 mb-2">
                <div className="col-span-5">Descripción</div>
                <div className="col-span-2">Cantidad</div>
                <div className="col-span-3">Precio</div>
                <div className="col-span-2">Acción</div>
              </div>
              
              {formData.items.map((item) => (
                <div key={item.id} className="grid grid-cols-12 gap-4 text-sm py-2 border-b border-gray-200 last:border-b-0">
                  <div className="col-span-5">{item.description}</div>
                  <div className="col-span-2">{item.quantity}</div>
                  <div className="col-span-3">S/ {item.unitPrice.toFixed(2)}</div>
                  <div className="col-span-2 flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeItem(item.id)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end space-y-1 text-sm">
              <div className="text-right">
                <div>Subtotal: S/ {totalAmount.toFixed(2)}</div>
                <div>IGV (18%): S/ {igv.toFixed(2)}</div>
                <div className="font-semibold text-lg">Total: S/ {grandTotal.toFixed(2)}</div>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div>
          <Label htmlFor="observations">Observaciones</Label>
          <Textarea
            id="observations"
            value={formData.observations || ''}
            onChange={!isCajero ? (e) => setFormData(prev => ({ ...prev, observations: e.target.value })) : undefined}
            readOnly={isCajero}
            onClick={isCajero ? () => openKeypad('text', 'observations', 'Observaciones', formData.observations || '') : undefined}
            rows={3}
            placeholder="Observaciones adicionales..."
            className={isCajero ? 'cursor-pointer' : ''}
          />
        </div>
      </Card>

      <div className="flex justify-end gap-4">
        {showCancelButton && (
          <Button type="button" variant="outline">
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          disabled={loading || formData.items.length === 0}
          className="bg-macartur-primary hover:bg-macartur-primary/90"
        >
          {loading ? 'Emitiendo...' : 'Emitir Comprobante'}
        </Button>
      </div>

      {/* Keypads para cajero */}
      {isCajero && (
        <>
          <TextKeypad
            isOpen={activeKeypad?.type === 'text'}
            onClose={() => setActiveKeypad(null)}
            onSubmit={(value) => handleKeypadSubmit(value)}
            title={activeKeypad?.title || ''}
            initialValue={keypadInitialValue}
          />
          <DecimalKeypad
            isOpen={activeKeypad?.type === 'decimal'}
            onClose={() => setActiveKeypad(null)}
            onSubmit={(value) => handleKeypadSubmit(value)}
            title={activeKeypad?.title || ''}
            initialValue={keypadInitialValue}
          />
          <DNIKeypad
            isOpen={activeKeypad?.type === 'dni'}
            onClose={() => setActiveKeypad(null)}
            onSubmit={(value) => handleKeypadSubmit(value)}
            title={activeKeypad?.title || ''}
            initialValue={keypadInitialValue}
          />
        </>
      )}
    </form>
  );
};